/**
 * Componente de sección de API Calls
 * API Calls section component
 */
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { AgGridTable } from '@shared/components/ui/AgGridTable';
import { GetRowIdParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { CourierStatusResponse, OutdatedOrder } from '../../models/TrackingApiResponse';
import { CourierStatusCard } from './CourierStatusCard';

interface ApiCallsSectionProps {
  courierStatus?: CourierStatusResponse;
  outdatedOrders?: OutdatedOrder[];
  total?: number;
  isLoading?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  selectedCourierId?: number | null;
  onCourierClick?: (courierId: number) => void;
}

export const ApiCallsSection = ({
  courierStatus,
  outdatedOrders = [],
  total = 0,
  isLoading = false,
  isFetching = false,
  isFetchingNextPage = false,
  fetchNextPage,
  hasNextPage = false,
  selectedCourierId,
  onCourierClick,
}: ApiCallsSectionProps) => {
  const { t } = useTranslation('trackingModule');
  const { filteredColumnDefs } = useAgGridColumns({
    tableType: 'outdatedOrders',
  });

  return (
    <div className="space-y-6">
      {/* Estado de Couriers */}
      <div data-testid="courier-status-section">
        {isLoading && !courierStatus ? (
          <div className="text-center py-8 text-subtext">{t('loading.message')}</div>
        ) : !courierStatus?.data || courierStatus.data.length === 0 ? (
          <EmptyView
            type="emptyState"
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={t('empty.no_data.title')}
            description={t('empty.no_data.description')}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courierStatus.data.map((courier) => (
              <CourierStatusCard
                key={courier.courier_id}
                courier={courier}
                onCourierClick={onCourierClick}
                isSelected={selectedCourierId === courier.courier_id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pedidos Desfasados */}
      <SectionBox data-testid="outdated-orders-section">
        <h2 className="text-lg font-semibold text-title mb-2">{t('api_calls.outdated_orders.title')}</h2>
        <p className="text-sm text-subtext mb-4">{t('api_calls.outdated_orders.description')}</p>

        {isLoading && outdatedOrders.length === 0 ? (
          <div className="text-center py-8 text-subtext">{t('loading.message')}</div>
        ) : total === 0 ? (
          <EmptyView
            type="emptyState"
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={t('empty.no_data.title')}
            description={t('empty.no_data.description')}
          />
        ) : (
          <div
            className={`transition-opacity duration-300 ${
              isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
            }`}
          >
            <AgGridTable
              data={outdatedOrders}
              columnDefs={filteredColumnDefs}
              height="calc(100vh - 350px)"
              enableSelection={false}
              rowHeight={60}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              gridOptions={{
                getRowId: (params: GetRowIdParams) => `outdated-${params.data.order_id}-${params.data.tracking_number}`,
              }}
            />
          </div>
        )}
      </SectionBox>
    </div>
  );
};
